import React, { useState, useCallback, useRef } from 'react';
import { ImagePlus, X, Image as ImageIcon } from 'lucide-react';

interface GalleryUploadProps {
  label?: string;
  description?: string;
  onChange?: (files: File[]) => void;
  maxFiles?: number;
}

export function GalleryUpload({ label, description, onChange, maxFiles = 10 }: GalleryUploadProps) {
  const [images, setImages] = useState<Array<{ file: File; url: string }>>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFiles = (newFiles: File[]) => {
    const validFiles = newFiles.filter(f => f.type.startsWith('image/'));
    const mapped = validFiles.map(file => ({
      file,
      url: URL.createObjectURL(file)
    }));
    
    setImages(prev => {
      const combined = [...prev, ...mapped].slice(0, maxFiles);
      if (onChange) onChange(combined.map(img => img.file));
      return combined;
    });
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    if (e.dataTransfer.files) {
      handleFiles(Array.from(e.dataTransfer.files));
    }
  }, []);

  const removeImage = (index: number) => {
    setImages(prev => {
      const newImages = prev.filter((_, i) => i !== index);
      if (onChange) onChange(newImages.map(img => img.file));
      return newImages;
    });
  };

  return (
    <div className="flex flex-col gap-2 w-full">
      {(label || description) && (
        <div>
          {label && <label className="text-sm font-medium text-gray-900">{label}</label>}
          {description && <p className="text-xs text-gray-500">{description}</p>}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 mt-2">
        {images.map((img, i) => (
          <div key={i} className="relative aspect-square rounded-xl overflow-hidden border border-gray-200 group bg-gray-100">
            <img src={img.url} alt={`Upload ${i}`} className="w-full h-full object-cover" />
            <button 
              type="button" 
              onClick={() => removeImage(i)}
              className="absolute top-2 right-2 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
            >
              <X size={14} />
            </button>
          </div>
        ))}

        {images.length < maxFiles && (
          <div 
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            className="aspect-square rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 flex flex-col items-center justify-center cursor-pointer hover:border-indigo-500 hover:bg-indigo-50/50 transition-colors group"
          >
            <ImagePlus size={28} className="text-gray-400 group-hover:text-indigo-500 mb-2 transition-colors" />
            <span className="text-xs font-medium text-gray-500 group-hover:text-indigo-600 px-2 text-center">Adicionar Imagem</span>
          </div>
        )}
      </div>

      <input 
        ref={fileInputRef}
        type="file" 
        accept="image/*" 
        multiple 
        className="hidden" 
        onChange={(e) => e.target.files && handleFiles(Array.from(e.target.files))}
      />
    </div>
  );
}
